import { alpha } from "@mui/material/styles";
import { Box, Grid, Paper, Skeleton, Stack, Typography } from "@mui/material";
import {
  CheckCircleOutlineOutlined,
  CloudOffOutlined,
  ErrorOutlineOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import CardDataComponent from "../../../components/overview/cardDataComponent";

// Estado de carregamento / "sem dados da API" (usado quando a fonte é só a API).
export function EstadoApi({
  styles,
  theme,
  loading = false,
  mensagem = "Sem dados da API",
  detalhe = "Esta seção ainda não é fornecida pela API.",
  altura = 220,
}) {
  return (
    <Paper
      sx={{ ...styles.chartCard, backgroundColor: theme.palette.background.paper }}
      elevation={theme.palette.mode === "dark" ? 0 : 3}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        gap="12px"
        sx={{ minHeight: altura, py: 4 }}
      >
        {loading ? (
          <>
            <Skeleton
              variant="circular"
              width={42}
              height={42}
              animation="wave"
              sx={{ bgcolor: alpha(theme.palette.text.primary, 0.08) }}
            />
            <Skeleton
              variant="rounded"
              width="52%"
              height={18}
              animation="wave"
              sx={{ bgcolor: alpha(theme.palette.text.primary, 0.08), borderRadius: "6px" }}
            />
            <Skeleton
              variant="rounded"
              width="72%"
              height={13}
              animation="wave"
              sx={{ bgcolor: alpha(theme.palette.text.primary, 0.06), borderRadius: "6px" }}
            />
          </>
        ) : (
          <>
            <CloudOffOutlined
              sx={{ fontSize: 34, color: theme.palette.text.secondary }}
            />
            <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{mensagem}</Typography>
            <Typography
              sx={{ fontSize: 12, color: theme.palette.text.secondary, textAlign: "center" }}
            >
              {detalhe}
            </Typography>
          </>
        )}
      </Stack>
    </Paper>
  );
}

export function EmptyChartState({
  theme,
  title = "Sem dados",
  detail = "Não foram encontrados dados para este gráfico no período selecionado.",
  altura = 286,
}) {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      gap="10px"
      sx={{
        minHeight: altura,
        px: 2,
        py: 4,
        borderRadius: "12px",
        border: `1px dashed ${alpha(theme.palette.text.primary, 0.16)}`,
        backgroundColor: alpha(theme.palette.text.primary, 0.025),
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 54,
          height: 54,
          borderRadius: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: theme.palette.text.secondary,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <CloudOffOutlined sx={{ fontSize: 30 }} />
      </Box>
      <Typography sx={{ fontSize: 15, fontWeight: 800, color: theme.palette.text.primary }}>
        {title}
      </Typography>
      <Typography
        sx={{
          maxWidth: 360,
          fontSize: 12.5,
          lineHeight: 1.45,
          color: theme.palette.text.secondary,
        }}
      >
        {detail}
      </Typography>
    </Stack>
  );
}

// Skeleton genérico no tamanho/formato do card: um único retângulo arredondado.
export function CardSkeleton({ theme, height = 286, radius = "16px" }) {
  return (
    <Skeleton
      variant="rounded"
      width="100%"
      height={height}
      animation="wave"
      sx={{ bgcolor: alpha(theme.palette.text.primary, 0.08), borderRadius: radius }}
    />
  );
}

export function ReportTabSkeleton({
  theme,
  columns = { xs: 2, sm: 4, md: 6, lg: 10, xl: 10 },
  full = { xs: 2, sm: 4, md: 6, lg: 10 },
  half = { xs: 2, sm: 4, md: 6, lg: 5 },
  metric = { xs: 2, sm: 2, md: 2, lg: 2 },
  metrics = 5,
  charts = 4,
}) {
  // Cada slot é um retângulo no tamanho/formato do card.
  return (
    <Grid container spacing={1} columns={columns} sx={{ maxWidth: "100%", minWidth: 0 }}>
      {Array.from({ length: metrics }).map((_, idx) => (
        <Grid item key={`metric-${idx}`} {...metric}>
          <CardSkeleton theme={theme} height={110} />
        </Grid>
      ))}
      {Array.from({ length: charts }).map((_, idx) => (
        <Grid item key={`chart-${idx}`} {...(idx === charts - 1 ? full : half)}>
          <CardSkeleton theme={theme} height={374} />
        </Grid>
      ))}
    </Grid>
  );
}

/* ----------------------------- formatters ----------------------------- */
export const formatInt = (value) => Number(value || 0).toLocaleString("pt-BR");

export const formatPct = (value) => `${(Number(value || 0) * 100).toFixed(1)}%`;

export const formatTempo = (segundos) => {
  const total = Number(segundos || 0);
  const dias = Math.floor(total / 86400);
  const horas = Math.floor((total % 86400) / 3600);
  const minutos = Math.floor((total % 3600) / 60);
  const partes = [];
  if (dias) partes.push(`${dias}d`);
  if (horas) partes.push(`${horas}h`);
  partes.push(`${minutos}m`);
  return partes.join(" ");
};

/* ----------------------------- table styles ----------------------------- */
export const tableHeadCell = (theme, align) => ({
  textAlign: align,
  padding: "10px 12px",
  fontSize: 11,
  fontWeight: 800,
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
  borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
  whiteSpace: "nowrap",
});

export const tableBodyCell = (theme, align, idx) => ({
  textAlign: align,
  padding: "10px 12px",
  fontSize: 13,
  color: theme.palette.text.primary,
  minWidth: 0,
  borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
  backgroundColor:
    idx % 2 === 1 ? alpha(theme.palette.text.primary, 0.02) : "transparent",
  whiteSpace: "nowrap",
});

/* ----------------------------- components ----------------------------- */
export function MetricCard({ styles, theme, icon: Icon, accent, title, value, helper, loading = false, paramsColor = null }) {
  if (loading) {
    return <CardSkeleton theme={theme} height={110} />;
  }

  let colorCard = "inputData";
  if (accent === theme.palette.error.main || accent === theme.palette.warning.main) {
    colorCard = "statusDown";
  } else if (accent === theme.palette.success.main) {
    colorCard = "statusUp";
  }

  return (
    <CardDataComponent
      cardTile={title}
      cardValue={value}
      helperText={helper}
      icon={Icon}
      colorCard={colorCard}
      paramsColor={paramsColor}
    />
  );
}

// Cores do card no padrão semáforo (verde/laranja/vermelho) a partir do %.
// Fundo sólido na cor do semáforo; ícone/valor em branco para contraste
// (o título do card já é branco — theme.palette.white.main).
export const semaforoCardColor = (theme, pctColorFn, pct) => {
  const cor = pctColorFn(theme, pct);
  return {
    card: alpha(cor, 1),
    container: alpha(theme.palette.white.main, 0.24),
    icon: theme.palette.white.main,
  };
};

// Legenda das faixas de reincidência (semáforo). Usa ícone DIFERENTE por faixa
// para não depender só da cor (acessível a daltônicos).
export function SemaforoLegend({ theme, sx }) {
  const bands = [
    {
      icon: CheckCircleOutlineOutlined,
      color: theme.palette.success.main,
      label: "OK",
      range: "< 20%",
    },
    {
      icon: WarningAmberOutlined,
      color: theme.palette.warning.main,
      label: "Atenção",
      range: "20–30%",
    },
    {
      icon: ErrorOutlineOutlined,
      color: theme.palette.error.main,
      label: "Crítico",
      range: "≥ 30%",
    },
  ];
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      alignItems="center"
      sx={{ gap: "8px 18px", justifyContent: "center", ...sx }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          lineHeight: 1,
          color: theme.palette.text.secondary,
        }}
      >
        Faixas de reincidência
      </Typography>
      {bands.map((b) => (
        <Stack key={b.label} direction="row" alignItems="center" gap="6px">
          <b.icon sx={{ fontSize: 18, color: b.color, display: "block" }} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1, color: b.color }}>
            {b.label}
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1, color: theme.palette.text.secondary }}>
            {b.range}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

export function SectionTitle({ theme, title, helper, icon: Icon }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        mt: "8px",
        mb: "2px",
        pb: "6px",
        borderBottom: `2px solid ${alpha(theme.palette.text.primary, 0.08)}`,
      }}
    >
      {Icon && (
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "9px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            color: theme.palette.primary.main,
          }}
        >
          <Icon sx={{ fontSize: 18 }} />
        </Box>
      )}
      <Box>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>
        {helper && (
          <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary }}>
            {helper}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export function ChartCard({
  styles,
  theme,
  title,
  subtitle,
  topRight,
  children,
  loading = false,
  noData = false,
  noDataTitle = "Sem dados",
  noDataMessage = "Não foram encontrados dados para este gráfico no período selecionado.",
  emptyHeight,
  sx,
}) {
  // Carregando: um único retângulo no tamanho/formato do card (sem cabeçalho).
  if (loading) {
    return <CardSkeleton theme={theme} height={(emptyHeight || 286) + 88} />;
  }

  return (
    <Paper
      sx={{ ...styles.chartCard, backgroundColor: theme.palette.background.paper, ...sx }}
      elevation={theme.palette.mode === "dark" ? 0 : 3}
    >
      <Box sx={styles.cardHeader}>
        <Box>
          <Typography sx={styles.cardTitle}>{title}</Typography>
          {subtitle && <Typography sx={styles.cardSubtitle}>{subtitle}</Typography>}
        </Box>
        {topRight}
      </Box>
      {noData ? (
        <EmptyChartState
          theme={theme}
          title={noDataTitle}
          detail={noDataMessage}
          altura={emptyHeight || 286}
        />
      ) : (
        children
      )}
    </Paper>
  );
}
